import type { CardType, Difficulty, Focus, GenerationOptions, Grounding } from "../lib/types";

const COUNTS = [10, 25, 50, 100, 200];

const DIFFICULTIES: { value: Difficulty; label: string }[] = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
  { value: "mixed", label: "Mixed" },
];

const TYPES: { value: CardType; label: string; hint: string }[] = [
  { value: "basic", label: "Basic", hint: "Question → answer" },
  { value: "basic_reversed", label: "Reversed", hint: "Both directions, for term ↔ meaning" },
  { value: "cloze", label: "Cloze", hint: "Fill in the blank, in context" },
  { value: "image_occlusion", label: "Image occlusion", hint: "Hide labels on a diagram" },
];

const FOCUSES: { value: Focus; label: string }[] = [
  { value: "key_concepts", label: "Key concepts" },
  { value: "exam_prep", label: "Exam preparation" },
  { value: "definitions", label: "Definitions" },
  { value: "processes", label: "Processes" },
  { value: "memorisation", label: "Memorisation" },
  { value: "everything", label: "Everything" },
];

const GROUNDING: { value: Grounding; label: string; hint: string }[] = [
  {
    value: "strict",
    label: "Strict",
    hint: "Only what is in your material. Nothing added, even well-known facts.",
  },
  {
    value: "balanced",
    label: "Balanced",
    hint: "Your material is the basis; wording may be clarified.",
  },
  {
    value: "open",
    label: "Open",
    hint: "May supplement with outside knowledge, tagged and marked low-confidence.",
  },
];

export default function OptionsPanel({
  options,
  onChange,
  disabled,
}: {
  options: GenerationOptions;
  onChange: (next: GenerationOptions) => void;
  disabled?: boolean;
}) {
  const set = <K extends keyof GenerationOptions>(key: K, value: GenerationOptions[K]) =>
    onChange({ ...options, [key]: value });

  const toggleType = (type: CardType) => {
    const has = options.card_types.includes(type);
    const next = has ? options.card_types.filter((t) => t !== type) : [...options.card_types, type];
    set("card_types", next.length ? next : [type]);
  };

  const custom = !COUNTS.includes(options.card_count);
  const grounding = GROUNDING.find((g) => g.value === options.grounding)!;

  return (
    <fieldset
      disabled={disabled}
      style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
    >
      <label className="field">
        <span className="label">How many cards (at most)</span>
        <div className="row row-wrap">
          <div className="segmented">
            {COUNTS.map((count) => (
              <button
                key={count}
                type="button"
                aria-pressed={options.card_count === count}
                onClick={() => set("card_count", count)}
              >
                {count}
              </button>
            ))}
            <button type="button" aria-pressed={custom} onClick={() => set("card_count", 75)}>
              Custom
            </button>
          </div>
          {custom && (
            <input
              type="number"
              min={1}
              max={1000}
              value={options.card_count}
              style={{ width: 90 }}
              onChange={(e) => set("card_count", Math.max(1, Number(e.target.value) || 1))}
            />
          )}
        </div>
        <span className="hint">
          A ceiling, not a quota. If your material only supports 30 good cards, you get 30.
        </span>
      </label>

      <label className="field">
        <span className="label">Difficulty</span>
        <div className="segmented">
          {DIFFICULTIES.map((d) => (
            <button
              key={d.value}
              type="button"
              aria-pressed={options.difficulty === d.value}
              onClick={() => set("difficulty", d.value)}
            >
              {d.label}
            </button>
          ))}
        </div>
      </label>

      <div className="field">
        <span className="label">Card types</span>
        <div className="grid grid-2" style={{ gap: 4 }}>
          {TYPES.map((type) => (
            <label key={type.value} className="checkline">
              <input
                type="checkbox"
                checked={options.card_types.includes(type.value)}
                onChange={() => toggleType(type.value)}
              />
              <span>
                {type.label}
                <span className="faint small" style={{ display: "block", lineHeight: 1.3 }}>
                  {type.hint}
                </span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <label className="field">
        <span className="label">Focus</span>
        <select value={options.focus} onChange={(e) => set("focus", e.target.value as Focus)}>
          {FOCUSES.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
      </label>

      <details className="disclosure" style={{ marginTop: 4 }}>
        <summary>Advanced</summary>
        <div style={{ paddingTop: 10 }}>
          <label className="field">
            <span className="label">Source grounding</span>
            <div className="segmented">
              {GROUNDING.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  aria-pressed={options.grounding === g.value}
                  onClick={() => set("grounding", g.value)}
                >
                  {g.label}
                </button>
              ))}
            </div>
            <span className="hint">{grounding.hint}</span>
          </label>

          <label className="field">
            <span className="label">
              Minimum confidence — {Math.round(options.min_confidence * 100)}%
            </span>
            <input
              type="range"
              min={0}
              max={95}
              step={5}
              value={Math.round(options.min_confidence * 100)}
              onChange={(e) => set("min_confidence", Number(e.target.value) / 100)}
            />
            <span className="hint">
              Cards below this are flagged for review rather than silently dropped.
            </span>
          </label>

          <label className="field">
            <span className="label">
              Duplicate sensitivity — {Math.round(options.duplicate_threshold * 100)}%
            </span>
            <input
              type="range"
              min={60}
              max={99}
              step={1}
              value={Math.round(options.duplicate_threshold * 100)}
              onChange={(e) => set("duplicate_threshold", Number(e.target.value) / 100)}
            />
            <span className="hint">Lower catches more near-duplicates, at the risk of false positives.</span>
          </label>

          <label className="checkline" style={{ marginBottom: 12 }}>
            <input
              type="checkbox"
              checked={options.organise_into_subdecks}
              onChange={(e) => set("organise_into_subdecks", e.target.checked)}
            />
            <span>Organise cards into subdecks by topic</span>
          </label>

          <label className="field">
            <span className="label">Extra instructions (optional)</span>
            <textarea
              value={options.extra_instructions}
              placeholder="e.g. Focus on the parts covered in lecture 3. Skip the historical background."
              onChange={(e) => set("extra_instructions", e.target.value)}
            />
          </label>
        </div>
      </details>
    </fieldset>
  );
}
